import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { mediaApi, editorApi, folderApi, adminApi, starredApi, permissionApi, getAccessToken } from '../lib/api';
import api from '../lib/api';
import { MediaFile, Editor } from '../types';
import { formatBytes, formatDate, canUseStudio } from '../lib/utils';
import { Image as ImageIcon, Video, X, Download, Trash2, Info, PackageOpen, Calendar, Filter, Clock, FolderInput, Share2, Star, LayoutGrid, List, FileText, Tag, FolderPlus, BarChart3 } from 'lucide-react';
import { useAuth, isAdminRole } from '../contexts/AuthContext';
import { useRBAC } from '../hooks/useRBAC';
import { BulkMetadataEditor } from '../components/BulkMetadataEditor';
import { DownloadCodeModal } from '../components/DownloadCodeModal';
import { MetadataViewer } from '../components/MetadataViewer';
import { AnalyticsMetricsPanel } from '../components/AnalyticsMetricsPanel';
import { FolderTree } from '../components/FolderTree';
import { Breadcrumb } from '../components/Breadcrumb';
import { FolderCard } from '../components/FolderCard';
import { CreateFolderModal } from '../components/CreateFolderModal';
import { FolderContextMenu } from '../components/FolderContextMenu';
import { FileContextMenu } from '../components/FileContextMenu';
import { AdvancedFilterPanel } from '../components/AdvancedFilterPanel';
import { useMediaFilters } from '../hooks/useMediaFilters';
import { BatchUploadModal } from '../components/BatchUploadModal';
import { VersionHistoryModal } from '../components/VersionHistoryModal';
import { ShareDialog } from '../components/ShareDialog';
import { FolderPickerModal } from '../components/FolderPickerModal';
import { RenameDialog } from '../components/RenameDialog';
import { PropertiesPanel } from '../components/PropertiesPanel';
import { ActivityTimeline } from '../components/ActivityTimeline';
import { CommentsPanel } from '../components/CommentsPanel';
import { FileTagsManager } from '../components/FileTagsManager';
import { MediaLightbox } from '../components/MediaLightbox';
import { AddToCollectionModal } from '../components/AddToCollectionModal';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { VERTICALS } from '../constants/verticals';
import { useDownloadProgress } from '../contexts/DownloadProgressContext';
import { BulkDownloadConfirmModal } from '../components/BulkDownloadConfirmModal';

// Small self-contained badge that fetches test status for a single media file
function TestStatusBadge({ fileId }: { fileId: string }) {
  const [testStatus, setTestStatus] = useState<string | null>(null);
  useEffect(() => {
    api.get(`/creative-tests/${fileId}`)
      .then(res => { const s = res.data?.data?.status; if (s) setTestStatus(s); })
      .catch(() => {});
  }, [fileId]);
  if (!testStatus) return null;
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
      testStatus === 'tested' ? 'bg-green-100 text-green-700' :
      testStatus === 'scheduled' ? 'bg-yellow-100 text-yellow-700' :
      'bg-red-100 text-red-700'
    }`}>
      {testStatus}
    </span>
  );
}

interface FolderNode {
  id: string;
  name: string;
  created_at: string;
  color?: string;
  is_locked?: boolean;
  locked_by?: string;
  team_id?: string;
  team_name?: string;
}

interface BreadcrumbItem {
  id: string;
  name: string;
  level: number;
}

type ViewMode = 'grid' | 'list';

export function MediaLibraryPage() {
  const { user, featureFlags, authReady } = useAuth();
  const [downloadCodeFile, setDownloadCodeFile] = useState<MediaFile | null>(null);
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [folders, setFolders] = useState<FolderNode[]>([]);
  const [editors, setEditors] = useState<Editor[]>([]);
  const [buyers, setBuyers] = useState<Array<{ id: string; name: string }>>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEditor, setSelectedEditor] = useState('');
  const [selectedVertical, setSelectedVertical] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const filesPerPage = 12;

  // View mode state with localStorage persistence
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('mediaLibraryViewMode');
    return (saved === 'list' || saved === 'grid') ? saved : 'grid';
  });

  // Folder navigation state
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([]);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [createFolderParentId, setCreateFolderParentId] = useState<string | null>(null);

  // Folder context menu state
  const [contextMenuFolder, setContextMenuFolder] = useState<FolderNode | null>(null);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [folderTreeRefreshKey, setFolderTreeRefreshKey] = useState(0);

  // File context menu state
  const [contextMenuFile, setContextMenuFile] = useState<MediaFile | null>(null);
  const [fileContextMenuPosition, setFileContextMenuPosition] = useState({ x: 0, y: 0 });

  // Bulk editor state - checkboxes always visible
  const [selectionMode, setSelectionMode] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  const [showBulkEditor, setShowBulkEditor] = useState(false);
  const folderRefsMap = useRef<Map<string, HTMLElement>>(new Map());

  // Metadata viewer state
  const [metadataViewerFile, setMetadataViewerFile] = useState<{
    id: string;
    filename: string;
    metadata: Record<string, any>;
  } | null>(null);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState<string | null>(null);
  const [bulkDownloadModalState, setBulkDownloadModalState] = useState<{ open: boolean; fileIds: string[] }>({ open: false, fileIds: [] });
  const { startDownload, isDownloadingFiles } = useDownloadProgress();

  // Version history state
  const [versionHistoryFile, setVersionHistoryFile] = useState<{
    id: string;
    filename: string;
  } | null>(null);

  // Share dialog state
  const [shareDialogFile, setShareDialogFile] = useState<{
    id: string;
    name: string;
    type: 'file' | 'folder';
  } | null>(null);

  // Drag and drop state
  const [draggedFileIds, setDraggedFileIds] = useState<string[]>([]);

  // Advanced filters
  const filters = useMediaFilters();
  const [showFilters, setShowFilters] = useState(false);

  // Folder picker modal state
  const [showFolderPicker, setShowFolderPicker] = useState(false);
  const [folderPickerOperation, setFolderPickerOperation] = useState<'copy' | 'move'>('copy');

  // Rename dialog state
  const [renameDialog, setRenameDialog] = useState<{
    isOpen: boolean;
    resourceType: 'file' | 'folder';
    resourceId: string;
    currentName: string;
  }>({
    isOpen: false,
    resourceType: 'file',
    resourceId: '',
    currentName: ''
  });

  // Properties Panel state
  const [propertiesPanel, setPropertiesPanel] = useState<{
    isOpen: boolean;
    resourceType: 'file' | 'folder';
    resourceId: string;
  }>({
    isOpen: false,
    resourceType: 'file',
    resourceId: ''
  });

  // Activity timeline state
  const [activityTimelineFile, setActivityTimelineFile] = useState<{
    id: string;
    filename: string;
  } | null>(null);

  // Comments panel state
  const [commentsPanel, setCommentsPanel] = useState<{
    isOpen: boolean;
    fileId: string;
    fileName: string;
  }>({
    isOpen: false,
    fileId: '',
    fileName: ''
  });

  // Media lightbox state
  const [lightbox, setLightbox] = useState<{
    isOpen: boolean;
    initialIndex: number;
  }>({
    isOpen: false,
    initialIndex: 0
  });

  // Tags manager state
  const [tagsManager, setTagsManager] = useState<{
    isOpen: boolean;
    mediaId: string;
    fileName: string;
  }>({
    isOpen: false,
    mediaId: '',
    fileName: ''
  });

  // Analytics metrics panel state
  const [analyticsPanel, setAnalyticsPanel] = useState<{
    isOpen: boolean;
    fileId: string;
    fileName: string;
    currentMetrics?: any;
  }>({
    isOpen: false,
    fileId: '',
    fileName: '',
    currentMetrics: null
  });

  // Add to collection modal state
  const [addToCollectionModal, setAddToCollectionModal] = useState<{
    isOpen: boolean;
    fileRequestUploadId: string;
  }>({
    isOpen: false,
    fileRequestUploadId: ''
  });

  // Admin org-wide browsing state
  const [orgViewMode, setOrgViewMode] = useState<'personal' | 'org-wide' | 'shared'>('personal');
  const [sharedFoldersList, setSharedFoldersList] = useState<any[]>([]);
  const [orgUsers, setOrgUsers] = useState<Array<{ id: string; name: string; email: string; role: string; file_count?: number }>>([]);
  const [selectedOrgUser, setSelectedOrgUser] = useState<string | null>(null);
  const [orgUserSearch, setOrgUserSearch] = useState('');

  // Lasso selection state
  const [lassoActive, setLassoActive] = useState(false);
  const [lassoStart, setLassoStart] = useState<{ x: number; y: number } | null>(null);
  const [lassoEnd, setLassoEnd] = useState<{ x: number; y: number } | null>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const cardRefsMap = useRef<Map<string, HTMLElement>>(new Map());

  const getLassoRect = useCallback(() => {
    if (!lassoStart || !lassoEnd) return null;
    return {
      left: Math.min(lassoStart.x, lassoEnd.x),
      top: Math.min(lassoStart.y, lassoEnd.y),
      right: Math.max(lassoStart.x, lassoEnd.x),
      bottom: Math.max(lassoStart.y, lassoEnd.y),
    };
  }, [lassoStart, lassoEnd]);

  // Lasso mouse handlers
  const handleLassoMouseDown = useCallback((e: React.MouseEvent) => {
    // Only start lasso on left click
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    // Don't start lasso if clicking on interactive elements
    if (target.closest('button, input, a, [role="button"], select, textarea')) return;
    e.preventDefault();
    setLassoActive(true);
    setLassoStart({ x: e.clientX, y: e.clientY });
    setLassoEnd({ x: e.clientX, y: e.clientY });
  }, []);

  // Use window-level listeners for move/up so lasso works even when mouse leaves container
  useEffect(() => {
    if (!lassoActive) return;

    const handleMouseMove = (e: MouseEvent) => {
      setLassoEnd({ x: e.clientX, y: e.clientY });
    };

    const handleMouseUp = () => {
      const rect = getLassoRect();
      if (rect && (Math.abs(rect.right - rect.left) > 5 || Math.abs(rect.bottom - rect.top) > 5)) {
        const intersectingIds: string[] = [];
        cardRefsMap.current.forEach((el, id) => {
          const cardRect = el.getBoundingClientRect();
          const intersects =
            cardRect.left < rect.right &&
            cardRect.right > rect.left &&
            cardRect.top < rect.bottom &&
            cardRect.bottom > rect.top;
          if (intersects) intersectingIds.push(id);
        });
        if (intersectingIds.length > 0) {
          setSelectedFiles(prev => {
            const newSet = new Set(prev);
            intersectingIds.forEach(id => newSet.add(id));
            return Array.from(newSet);
          });
        }
        // Also check folder cards for lasso selection
        const intersectingFolderIds: string[] = [];
        folderRefsMap.current.forEach((el, id) => {
          const cardRect = el.getBoundingClientRect();
          const folderIntersects =
            cardRect.left < rect.right &&
            cardRect.right > rect.left &&
            cardRect.top < rect.bottom &&
            cardRect.bottom > rect.top;
          if (folderIntersects) intersectingFolderIds.push(id);
        });
        if (intersectingFolderIds.length > 0) {
          setSelectedFolders(prev => {
            const newSet = new Set(prev);
            intersectingFolderIds.forEach(id => newSet.add(id));
            return Array.from(newSet);
          });
        }
      }
      setLassoActive(false);
      setLassoStart(null);
      setLassoEnd(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [lassoActive, getLassoRect]);

  const lassoRect = getLassoRect();

  // Role-based permissions (RBAC)
  const { can } = useRBAC();
  const userAdditionalRoles: string[] = (user as any)?.additional_roles || [];
  const adminLikeRoles = ['admin', 'team_lead', 'ceo', 'head_media_buying', 'creative_head'];
  const isAdmin = isAdminRole(user?.role) || adminLikeRoles.includes(user?.role || '') || userAdditionalRoles.some(r => adminLikeRoles.includes(r));
  const canUpload = can('media', 'upload');
  const canDeleteFile = (file: MediaFile) => can('media', 'delete') || file.editor_id === user?.id;

  // Filtered files based on search and vertical
  const filteredFiles = useMemo(() => {
    let result = files;

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      result = result.filter(file =>
        file.original_filename.toLowerCase().includes(search) ||
        file.description?.toLowerCase().includes(search) ||
        file.tags?.some(tag => tag.toLowerCase().includes(search))
      );
    }

    // Apply vertical filter
    // NOTE: This requires files to have vertical metadata - for now, this will filter
    // based on files that came from file/launch requests with vertical tags
    // TODO: Backend implementation needed for proper vertical filtering via JOINs
    if (selectedVertical) {
      // This is a placeholder - actual vertical filtering requires backend JOIN queries
      // with file_request_uploads -> file_requests -> file_request_verticals tables
      result = result;  // Keep all for now until backend filtering is implemented
    }

    return result;
  }, [files, searchTerm, selectedVertical]);

  // Handle URL parameters on mount (for direct folder/file links from Slack, etc.)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const folderIdFromUrl = params.get('folderId');
    const fileIdFromUrl = params.get('fileId');

    if (folderIdFromUrl) {
      setCurrentFolderId(folderIdFromUrl);
    }

    // If fileId is provided, we'll open it after files load
    // Store it in a variable to be checked in the files effect
    if (fileIdFromUrl) {
      console.log('File ID from URL parameter:', fileIdFromUrl);
      // Will be handled after files are loaded
      (window as any).__pendingFileId = fileIdFromUrl;
    }

    // Clean up URL after processing
    if (folderIdFromUrl || fileIdFromUrl) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Initial load: fetch buyers and tags
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        if (isAdmin) {
          const usersRes = await adminApi.getUsers();
          const allUsers = usersRes.data.data || [];
          const buyerUsers = allUsers.filter((u: any) => u.role === 'buyer');
          setBuyers(buyerUsers.map((u: any) => ({ id: u.id, name: u.name })));
        }

        // Extract unique tags from existing files (simplified approach)
        // In a production app, you might have a dedicated tags endpoint
        const filesRes = await mediaApi.getAll({});
        const allFiles = filesRes.data.data.files || [];
        const tags = new Set<string>();
        allFiles.forEach((file: any) => {
          if (file.tags && Array.isArray(file.tags)) {
            file.tags.forEach((tag: string) => tags.add(tag));
          }
        });
        setAvailableTags(Array.from(tags).sort());
      } catch (error) {
        console.error('Failed to fetch initial data:', error);
      }
    };

    fetchInitialData();
  }, []);

  // Fetch org users when org view is activated
  useEffect(() => {
    if (orgViewMode === 'org-wide' && isAdmin && orgUsers.length === 0) {
      const fetchOrgUsers = async () => {
        try {
          const res = await adminApi.getUsers();
          const allUsers = res.data.data || [];
          setOrgUsers(allUsers.map((u: any) => ({
            id: u.id,
            name: u.name,
            email: u.email,
            role: u.role,
          })));
        } catch (err) {
          console.error('Failed to fetch org users:', err);
        }
      };
      fetchOrgUsers();
    }
  }, [orgViewMode, isAdmin]);

  // Fetch shared folders when shared tab is activated
  useEffect(() => {
    if (orgViewMode === 'shared') {
      const fetchSharedFolders = async () => {
        try {
          const res = await permissionApi.getSharedWithMe();
          setSharedFoldersList(res.data.data || []);
        } catch (err) {
          console.error('Failed to fetch shared folders:', err);
          setSharedFoldersList([]);
        }
      };
      fetchSharedFolders();
    }
  }, [orgViewMode]);

  useEffect(() => {
    if (!authReady) return;            // wait for AuthContext hydration
    if (orgViewMode !== 'shared') {
      fetchData();
      setCurrentPage(1);
    }
  }, [filters.filters, currentFolderId, selectedOrgUser, orgViewMode, authReady]);

  // Debounced global search - refetch when search term changes
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData();
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handle opening file from URL parameter after files are loaded
  useEffect(() => {
    const pendingFileId = (window as any).__pendingFileId;
    if (pendingFileId && files.length > 0 && !lightbox.isOpen) {
      const fileIndex = files.findIndex((f: MediaFile) => f.id === pendingFileId);
      if (fileIndex !== -1) {
        console.log('Opening file from URL:', pendingFileId, 'at index:', fileIndex);
        setLightbox({
          isOpen: true,
          initialIndex: fileIndex
        });
        // Clear the pending file ID
        delete (window as any).__pendingFileId;
      }
    }
  }, [files, lightbox.isOpen]);

  const fetchData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      // Global search: when user is searching, fetch ALL files across all folders
      if (searchTerm.trim()) {
        const queryParams = filters.toQueryParams();
        // Don't restrict to any folder - search globally
        delete queryParams.folder_id;
        queryParams.search = searchTerm.trim();

        const [filesRes, editorsRes] = await Promise.all([
          mediaApi.getAll(queryParams),
          editorApi.getAll(),
        ]);

        setFiles(filesRes.data.data.files || []);
        setEditors(editorsRes.data.data || []);
        // Don't clear folders/breadcrumb during search - keep navigation state
      } else if (currentFolderId) {
        // Fetch folder contents and breadcrumb
        const [contentsRes, breadcrumbRes, editorsRes] = await Promise.all([
          folderApi.getContents(currentFolderId),
          folderApi.getBreadcrumb(currentFolderId),
          editorApi.getAll(),
        ]);

        const contents = contentsRes.data.data;
        setFolders(contents.folders || []);
        setFiles(contents.files || []);
        setBreadcrumb(breadcrumbRes.data.data || []);
        setEditors(editorsRes.data.data || []);
      } else {
        // Fetch root level with advanced filters
        const queryParams = filters.toQueryParams();

        // FIXED: When viewing root without folder filters, explicitly request root-level files only
        // This prevents files in subfolders from appearing at root level
        if (!queryParams.folder_id) {
          queryParams.folder_id = 'null';  // Special value to indicate "only root files"
        }

        // Admin org-wide: filter by selected user
        if (selectedOrgUser && orgViewMode === 'org-wide') {
          queryParams.uploaded_by = selectedOrgUser;
          // When viewing a specific user's files, show all (not just root)
          delete queryParams.folder_id;
        }

        const [filesRes, editorsRes] = await Promise.all([
          mediaApi.getAll(queryParams),
          editorApi.getAll(),
        ]);

        setFiles(filesRes.data.data.files || []);
        setEditors(editorsRes.data.data || []);
        setFolders([]);
        setBreadcrumb([]);
      }
    } catch (error: any) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFolderSelect = (folderId: string | null) => {
    setCurrentFolderId(folderId);
    // Folder navigation should always start with a clean selection — selecting
    // files in folder A and then opening folder B previously kept the A files
    // selected, which made bulk actions silently apply to invisible files.
    setSelectedFiles([]);
  };

  const handleCreateFolder = () => {
    setCreateFolderParentId(currentFolderId);
    setShowCreateFolderModal(true);
  };

  const handleFolderContextMenu = (folder: FolderNode, event: React.MouseEvent) => {
    event.preventDefault();
    setContextMenuFolder(folder);
    setContextMenuPosition({ x: event.clientX, y: event.clientY });
  };

  const handleRenameFolder = () => {
    if (!contextMenuFolder) return;
    setRenameDialog({
      isOpen: true,
      resourceType: 'folder',
      resourceId: contextMenuFolder.id,
      currentName: contextMenuFolder.name
    });
  };

  const handleDeleteFolder = () => {
    if (!contextMenuFolder) return;
    // eslint-disable-next-line no-restricted-globals
    const confirmed = confirm(`Delete folder "${contextMenuFolder.name}"? This will also delete all files inside.`);
    if (confirmed) {
      folderApi.delete(contextMenuFolder.id, true)
        .then(async () => {
          await fetchData();
          setFolderTreeRefreshKey(k => k + 1);
        })
        .catch((error) => alert('Failed to delete folder: ' + error.message));
    }
  };

  const handleCreateSubfolder = () => {
    if (!contextMenuFolder) return;
    setCreateFolderParentId(contextMenuFolder.id);
    setShowCreateFolderModal(true);
  };

  const handleFolderProperties = () => {
    if (!contextMenuFolder) return;
    setPropertiesPanel({
      isOpen: true,
      resourceType: 'folder',
      resourceId: contextMenuFolder.id
    });
  };

  const handleShareFolder = () => {
    if (!contextMenuFolder) return;
    setShareDialogFile({
      id: contextMenuFolder.id,
      name: contextMenuFolder.name,
      type: 'folder'
    });
  };

  const handleDownloadFolderZip = async () => {
    if (!contextMenuFolder) return;

    setDownloadingZip(true);
    try {
      const response = await folderApi.downloadFolder(contextMenuFolder.id);

      // Generate filename with date
      const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      const zipFilename = `${contextMenuFolder.name}-${date}.zip`;

      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = zipFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Failed to download folder as ZIP:', error);
      alert(error.response?.data?.error || 'Failed to download folder as ZIP');
    } finally {
      setDownloadingZip(false);
    }
  };

  const handleToggleLock = async () => {
    if (!contextMenuFolder) return;

    const isLocked = contextMenuFolder.is_locked;
    let reason: string | undefined;

    // If locking, ask for reason (optional)
    if (!isLocked) {
      const userInput = prompt('Reason for locking this folder (optional):');
      if (userInput === null) {
        // User clicked cancel
        return;
      }
      // Only set reason if user actually entered something
      reason = userInput.trim() || undefined;
    }

    try {
      await folderApi.toggleLock(contextMenuFolder.id, reason);
      await fetchData(); // Refresh data to show updated lock status
      alert(isLocked ? 'Folder unlocked successfully' : 'Folder locked successfully');
    } catch (error: any) {
      console.error('Failed to toggle folder lock:', error);
      alert(error.response?.data?.error || 'Failed to toggle folder lock');
    }
  };

  // File context menu handlers
  const handleFileContextMenu = (file: MediaFile, event: React.MouseEvent) => {
    event.preventDefault();
    setContextMenuFile(file);
    setFileContextMenuPosition({ x: event.clientX, y: event.clientY });
  };

  const handleFileDownload = () => {
    if (!contextMenuFile) return;
    handleDownload(contextMenuFile);
  };

  const handleFileShare = () => {
    if (!contextMenuFile) return;
    setShareDialogFile({
      id: contextMenuFile.id,
      name: contextMenuFile.original_filename,
      type: 'file'
    });
  };

  const handleFileStar = () => {
    if (!contextMenuFile) return;
    handleToggleStar(contextMenuFile.id, !contextMenuFile.is_starred);
  };

  const handleFileRename = () => {
    if (!contextMenuFile) return;
    setRenameDialog({
      isOpen: true,
      resourceType: 'file',
      resourceId: contextMenuFile.id,
      currentName: contextMenuFile.original_filename
    });
  };

  const handleFileMove = () => {
    if (!contextMenuFile) return;
    setSelectedFiles([contextMenuFile.id]);
    setFolderPickerOperation('move');
    setShowFolderPicker(true);
  };

  const handleFileCopy = () => {
    if (!contextMenuFile) return;
    setSelectedFiles([contextMenuFile.id]);
    setFolderPickerOperation('copy');
    setShowFolderPicker(true);
  };

  const handleFileVersions = () => {
    if (!contextMenuFile) return;
    setVersionHistoryFile({
      id: contextMenuFile.id,
      filename: contextMenuFile.original_filename
    });
  };

  const handleFileActivity = () => {
    if (!contextMenuFile) return;
    setActivityTimelineFile({
      id: contextMenuFile.id,
      filename: contextMenuFile.original_filename
    });
  };

  const handleFileDelete = () => {
    if (!contextMenuFile) return;
    setDeleteConfirmId(contextMenuFile.id);
  };

  const handleFileProperties = () => {
    if (!contextMenuFile) return;
    setPropertiesPanel({
      isOpen: true,
      resourceType: 'file',
      resourceId: contextMenuFile.id
    });
  };

  const handleFileComments = () => {
    if (!contextMenuFile) return;
    setCommentsPanel({
      isOpen: true,
      fileId: contextMenuFile.id,
      fileName: contextMenuFile.original_filename
    });
  };

  const handleFileTags = (file?: MediaFile) => {
    const targetFile = file || contextMenuFile;
    if (!targetFile) return;
    setTagsManager({
      isOpen: true,
      mediaId: targetFile.id,
      fileName: targetFile.original_filename
    });
  };

  // Triggers the actual file fetch + browser download
  const performDownload = async (downloadUrl: string, downloadFilename: string) => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const fullUrl = downloadUrl.startsWith('http') ? downloadUrl : `${API_BASE.replace(/\/api$/, '')}${downloadUrl}`;
      const token = getAccessToken();
      const response = await fetch(fullUrl, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download file. Please try again.');
    }
  };

  const handleDownload = async (file: MediaFile, stripMetadata = false) => {
    // Determine if download-code modal flow is required:
    //   Feature flag ON + user is buyer (and not admin/editor/QC) + file is post-cutoff date
    // Skip the modal for stripMetadata downloads (cleaning meta is independent of code).
    const userRoles = [user?.role, ...((user as any)?.additional_roles || [])].filter(Boolean) as string[];
    const isBuyerOnly = userRoles.includes('buyer') && !userRoles.some(r => ['admin','team_lead','ceo','head_media_buying','creative_head','creative','quality_check'].includes(r))
      || (featureFlags?.download_codes_enabled && userRoles.includes('admin'));
    const startDate = featureFlags?.download_codes_start_date ? new Date(featureFlags.download_codes_start_date) : null;
    const eligible = featureFlags?.download_codes_enabled && startDate && new Date(file.created_at) >= startDate;

    if (!stripMetadata && eligible && isBuyerOnly) {
      setDownloadCodeFile(file);
      return;
    }

    // Default direct-download flow (unchanged)
    const endpoint = stripMetadata ? 'download-clean' : 'download';
    const filename = stripMetadata ? `clean_${file.original_filename}` : file.original_filename;
    await performDownload(`/api/media/${file.id}/${endpoint}`, filename);
  };

  const handleDelete = async (fileId: string) => {
    try {
      await mediaApi.delete(fileId);
      setDeleteConfirmId(null);
      fetchData();
    } catch (error: any) {
      console.error('Delete failed:', error);
      alert(error.response?.data?.error || 'Failed to delete file');
    }
  };

  const toggleFileSelection = (fileId: string) => {
    setSelectedFiles(prev =>
      prev.includes(fileId)
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedFiles.length === files.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(files.map(f => f.id));
    }
  };

  const handleViewMetadata = async (file: MediaFile) => {
    try {
      const response = await mediaApi.extractMetadata(file.id);
      setMetadataViewerFile({
        id: file.id,
        filename: file.original_filename,
        metadata: response.data.data.metadata || {},
      });
    } catch (error: any) {
      console.error('Failed to extract metadata:', error);
      alert(error.response?.data?.error || 'Failed to extract metadata');
    }
  };

  const handleBulkDownloadZip = () => {
    if (selectedFiles.length === 0) return;
    if (isDownloadingFiles(selectedFiles)) return;
    setBulkDownloadModalState({ open: true, fileIds: selectedFiles });
  };

  const confirmBulkDownloadZip = async () => {
    const fileIds = bulkDownloadModalState.fileIds;
    setBulkDownloadModalState({ open: false, fileIds: [] });
    if (fileIds.length === 0) return;
    await startDownload({
      fileIds,
      label: `${fileIds.length} file${fileIds.length === 1 ? '' : 's'} from Media Library`,
    });
  };

  const handleBulkDelete = async () => {
    if (selectedFiles.length === 0) return;

    if (!window.confirm(`Delete ${selectedFiles.length} selected files? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await mediaApi.bulkDelete(selectedFiles);
      alert(response.data.message);
      setSelectedFiles([]);
      setSelectionMode(false);
      fetchData();
    } catch (error: any) {
      console.error('Failed to bulk delete:', error);
      alert(error.response?.data?.error || 'Failed to delete files');
    }
  };

  const handleBulkMoveToFolder = () => {
    if (selectedFiles.length === 0) return;
    setFolderPickerOperation('move');
    setShowFolderPicker(true);
  };

  const handleBulkCopyToFolder = () => {
    if (selectedFiles.length === 0) return;
    setFolderPickerOperation('copy');
    setShowFolderPicker(true);
  };

  const handleFolderPickerSelect = async (targetFolderId: string | null) => {
    try {
      // Handle bulk folder move
      if (selectedFolders.length > 0 && folderPickerOperation === 'move') {
        for (const folderId of selectedFolders) {
          await folderApi.update(folderId, { parent_id: targetFolderId } as any);
        }
        alert(`Successfully moved ${selectedFolders.length} folder(s)`);
        setSelectedFolders([]);
        await fetchData();
        setFolderTreeRefreshKey(k => k + 1);
      } else if (selectedFiles.length > 0) {
        // Handle file operations
        if (folderPickerOperation === 'move') {
          await mediaApi.bulkMove(selectedFiles, targetFolderId);
          alert(`Successfully moved ${selectedFiles.length} file(s)`);
        } else if (folderPickerOperation === 'copy') {
          await mediaApi.bulkCopy(selectedFiles, targetFolderId);
          alert(`Successfully copied ${selectedFiles.length} file(s)`);
        }
        setSelectedFiles([]);
        setSelectionMode(false);
        setContextMenuFile(null);
        fetchData();
      }
    } catch (error: any) {
      console.error(`Failed to ${folderPickerOperation}:`, error);
      alert(error.response?.data?.error || `Failed to ${folderPickerOperation}`);
    } finally {
      setShowFolderPicker(false);
    }
  };

  // Drag and drop handlers
  const handleFileDragStart = (e: React.DragEvent, fileId: string) => {
    if (selectedFiles.includes(fileId)) {
      setDraggedFileIds(selectedFiles);
    } else {
      setDraggedFileIds([fileId]);
    }
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleFolderDrop = async (e: React.DragEvent, targetFolderId: string) => {
    e.preventDefault();
    if (draggedFileIds.length === 0) return;

    try {
      await folderApi.moveFiles({
        file_ids: draggedFileIds,
        target_folder_id: targetFolderId,
      });
      setDraggedFileIds([]);
      setSelectedFiles([]);
      fetchData();
    } catch (error: any) {
      alert('Failed to move files: ' + error.message);
    }
  };

  const handleFolderDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const selectedFileObjects = files.filter(f => selectedFiles.includes(f.id));

  const handleToggleStar = async (fileId: string, isStarred: boolean) => {
    try {
      await starredApi.toggleStarred(fileId, isStarred);
      // Update local state
      setFiles(files.map(f => f.id === fileId ? { ...f, is_starred: isStarred } : f));
    } catch (error: any) {
      console.error('Failed to toggle star:', error);
      alert('Failed to update star status');
    }
  };

  const handleOpenAnalytics = (file: MediaFile) => {
    setAnalyticsPanel({
      isOpen: true,
      fileId: file.id,
      fileName: file.original_filename,
      currentMetrics: file.analytics_metrics
    });
  };

  const handleSaveAnalytics = async (metrics: any) => {
    const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');
    const token = getAccessToken();

    const response = await fetch(`${API_BASE_URL}/media/${analyticsPanel.fileId}/analytics`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ analytics_metrics: metrics })
    });

    if (!response.ok) {
      throw new Error('Failed to save analytics metrics');
    }

    // Refresh files to show updated metrics
    await fetchData();
    alert('Analytics metrics saved successfully!');
  };

  const handleRename = async (newName: string) => {
    if (renameDialog.resourceType === 'file') {
      await mediaApi.rename(renameDialog.resourceId, newName);
    } else {
      await folderApi.rename(renameDialog.resourceId, newName);
    }
    await fetchData();
  };

  const toggleViewMode = () => {
    const newMode: ViewMode = viewMode === 'grid' ? 'list' : 'grid';
    setViewMode(newMode);
    localStorage.setItem('mediaLibraryViewMode', newMode);
  };

  return (
    <DashboardLayout title="Media Library">
      <div className="flex h-[calc(100vh-4rem)]">
        {/* Folder Tree Sidebar */}
        <div className="w-64 flex-shrink-0">
          <FolderTree
            currentFolderId={currentFolderId}
            onFolderSelect={handleFolderSelect}
            onCreateFolder={handleCreateFolder}
            onFolderContextMenu={handleFolderContextMenu}
            refreshKey={folderTreeRefreshKey}
          />
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                {/* Admin org-wide toggle */}
                <div className="flex mt-2 border rounded-md overflow-hidden w-fit">
                  <button
                    onClick={() => { setOrgViewMode('personal'); setSelectedOrgUser(null); }}
                    className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                      orgViewMode === 'personal'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    My Library
                  </button>
                  {isAdmin && (
                  <button
                    onClick={() => setOrgViewMode('org-wide')}
                    className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                      orgViewMode === 'org-wide'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    Org-Wide Library
                    </button>
                  )}
                  </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {canUpload && (
                  <Button onClick={() => setShowUploadModal(true)}>
                    Upload Files
                  </Button>
                )}
                {selectedFiles.length > 0 && (
                  <>
                    {canUpload && (
                      <Button onClick={() => setShowBulkEditor(true)}>
                        Edit {selectedFiles.length} Selected
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={handleBulkDownloadZip}
                      disabled={isDownloadingFiles(selectedFiles)}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {isDownloadingFiles(selectedFiles)
                        ? 'Downloading…'
                        : `Download ZIP (${selectedFiles.length} file${selectedFiles.length !== 1 ? 's' : ''})`}
                    </Button>
                    {canUpload && (
                      <>
                        <Button
                          variant="outline"
                          onClick={handleBulkMoveToFolder}
                        >
                          <FolderInput className="w-4 h-4 mr-2" />
                          Move
                        </Button>
                        <Button
                          variant="outline"
                          onClick={handleBulkCopyToFolder}
                        >
                          <FolderInput className="w-4 h-4 mr-2" />
                          Copy
                        </Button>
                      </>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (selectedFiles.length > 0) {
                          setShareDialogFile({
                            id: selectedFiles[0],
                            name: `${selectedFiles.length} file${selectedFiles.length !== 1 ? 's' : ''}`,
                            type: 'file'
                          });
                        }
                      }}
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Share ({selectedFiles.length})
                    </Button>
                    <Button
                      variant="outline"
                      className="text-destructive hover:text-destructive"
                      onClick={handleBulkDelete}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete {selectedFiles.length}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setSelectedFiles([])}
                      title="Clear selection"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Clear
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Breadcrumb */}
            {breadcrumb.length > 0 && (
              <Breadcrumb items={breadcrumb} onNavigate={handleFolderSelect} />
            )}

            {/* Org-wide user browser */}
            {isAdmin && orgViewMode === 'org-wide' && !selectedOrgUser && (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-semibold">Browse by User</h2>
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={orgUserSearch}
                    onChange={(e) => setOrgUserSearch(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {orgUsers
                    .filter(u => !orgUserSearch || u.name.toLowerCase().includes(orgUserSearch.toLowerCase()) || u.email.toLowerCase().includes(orgUserSearch.toLowerCase()))
                    .map(u => (
                    <div
                      key={u.id}
                      onClick={() => setSelectedOrgUser(u.id)}
                      className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:shadow-md hover:border-blue-400 transition-all"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 font-semibold">
                          {u.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 dark:text-white truncate">{u.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{u.email}</p>
                          <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                            {u.role}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {orgUsers.length === 0 && (
                  <p className="text-muted-foreground text-sm">Loading users...</p>
                )}
              </div>
            )}

            {/* Org-wide: selected user header */}
            {isAdmin && orgViewMode === 'org-wide' && selectedOrgUser && (
              <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <button
                  onClick={() => setSelectedOrgUser(null)}
                  className="px-3 py-1.5 text-sm bg-white dark:bg-gray-800 border rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  ← Back to Users
                </button>
                <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Viewing files for: {orgUsers.find(u => u.id === selectedOrgUser)?.name || 'Unknown User'}
                </span>
              </div>
            )}

            {/* Selection Info */}
            {files.length > 0 && (
              <div className="flex items-center gap-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedFiles.length === files.length && files.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium">
                    Select All ({selectedFiles.length} / {files.length})
                  </span>
                </label>
                {selectedFiles.length > 0 && (
                  <>
                    <span className="text-sm text-blue-700 dark:text-blue-300">
                      {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
                    </span>
                    <button
                      onClick={() => setSelectedFiles([])}
                      className="text-xs px-2 py-1 rounded border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                    >
                      Clear selection
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Global Search and Vertical Filter */}
            <div className="flex gap-3 mb-4">
              {/* Global Search Box */}
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search files across all folders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Vertical Filter Dropdown */}
              <SearchableSelect
                value={selectedVertical}
                onChange={setSelectedVertical}
                placeholder="All Verticals"
                className="w-48"
                options={[
                  { value: '', label: 'All Verticals' },
                  ...[...VERTICALS].map(v => ({ value: v, label: v }))
                ]}
              />
            </div>

            {/* Filters and View Toggle */}
            <div className="flex gap-4 items-center">
              {/* Filter Button with Dropdown */}
              <div className="relative">
                <Button
                  variant={filters.hasActiveFilters ? "default" : "outline"}
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2"
                >
                  <Filter size={16} />
                  Filters
                  {filters.hasActiveFilters && (
                    <span className="ml-1 px-2 py-0.5 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-full">
                      {filters.activeFilterCount}
                    </span>
                  )}
                </Button>

                {/* Advanced Filter Dropdown Panel */}
                {showFilters && (
                  <AdvancedFilterPanel
                    onClose={() => setShowFilters(false)}
                    filters={filters.filters}
                    onFilterChange={filters.updateFilter}
                    onToggleMediaType={filters.toggleMediaType}
                    onToggleEditor={filters.toggleEditor}
                    onToggleBuyer={filters.toggleBuyer}
                    onToggleFolder={filters.toggleFolder}
                    onToggleTag={filters.toggleTag}
                    onClear={filters.clearFilters}
                    editors={editors}
                    buyers={buyers}
                    folders={folders}
                    availableTags={availableTags}
                  />
                )}
              </div>

              {filters.hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={filters.clearFilters}
                >
                  Clear All Filters
                </Button>
              )}

              {/* View Mode Toggle */}
              <div className="flex border rounded-md overflow-hidden">
                <button
                  onClick={() => {
                    setViewMode('grid');
                    localStorage.setItem('mediaLibraryViewMode', 'grid');
                  }}
                  className={`px-3 py-2 flex items-center gap-2 transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background hover:bg-accent'
                  }`}
                  title="Grid view"
                >
                  <LayoutGrid size={16} />
                  <span className="text-sm">Grid</span>
                </button>
                <button
                  onClick={() => {
                    setViewMode('list');
                    localStorage.setItem('mediaLibraryViewMode', 'list');
                  }}
                  className={`px-3 py-2 flex items-center gap-2 transition-colors ${
                    viewMode === 'list'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background hover:bg-accent'
                  }`}
                  title="List view"
                >
                  <List size={16} />
                  <span className="text-sm">List</span>
                </button>
              </div>
            </div>

            {/* Content */}
            {orgViewMode === 'shared' ? (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold">Shared With Me</h2>
                {sharedFoldersList.length === 0 ? (
                  <Card className="p-12 text-center">
                    <p className="text-muted-foreground">No folders have been shared with you yet</p>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {sharedFoldersList.map((item: any) => (
                      <div
                        key={item.folder_id || item.id}
                        className="bg-white dark:bg-gray-800 border rounded-lg p-4 cursor-pointer hover:border-blue-400 transition-colors"
                        onClick={() => {
                          setOrgViewMode('personal');
                          handleFolderSelect(item.folder_id || item.id);
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <FolderInput className="w-8 h-8 text-purple-500" />
                          <div className="min-w-0">
                            <p className="font-medium truncate">{item.folder_name || item.name}</p>
                            <p className="text-xs text-muted-foreground">
                              Shared by {item.shared_by_name || item.owner_name || 'Unknown'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Permission: {item.permission_type || item.permissions?.join(', ') || 'view'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : orgViewMode === 'org-wide' && !selectedOrgUser ? null : loading ? (
              <div className="flex items-center justify-center h-96">
                <p className="text-muted-foreground">Loading...</p>
              </div>
            ) : (
              <div ref={gridContainerRef} className="relative select-none" onMouseDown={handleLassoMouseDown}>
                {/* Lasso overlay */}
                {lassoActive && lassoRect && (
                  <div
                    style={{
                      position: 'fixed',
                      left: lassoRect.left,
                      top: lassoRect.top,
                      width: lassoRect.right - lassoRect.left,
                      height: lassoRect.bottom - lassoRect.top,
                      border: '2px dashed #3b82f6',
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      pointerEvents: 'none',
                      zIndex: 50,
                    }}
                  />
                )}
                {/* FOLDERS FIRST */}
                {folders.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold">Folders</h2>
                      {selectedFolders.length > 0 && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-blue-600 font-medium">
                            {selectedFolders.length} folder{selectedFolders.length !== 1 ? 's' : ''} selected
                          </span>
                          <button
                            onClick={() => {
                              // Share the first selected folder (share dialog handles one at a time)
                              const folderId = selectedFolders[0];
                              const folder = folders.find((f: any) => f.id === folderId);
                              if (folder) {
                                setShareDialogFile({ id: folder.id, name: folder.name, type: 'folder' });
                              }
                            }}
                            className="px-3 py-1.5 text-xs bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                          >
                            Share
                          </button>
                          <button
                            onClick={() => {
                              setFolderPickerOperation('move');
                              setShowFolderPicker(true);
                            }}
                            className="px-3 py-1.5 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                          >
                            Move
                          </button>
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Delete ${selectedFolders.length} selected folder(s)? This will also delete all files inside.`)) return;
                              try {
                                for (const folderId of selectedFolders) {
                                  await folderApi.delete(folderId, true);
                                }
                                setSelectedFolders([]);
                                await fetchData();
                                setFolderTreeRefreshKey(k => k + 1);
                              } catch (error: any) {
                                alert('Failed to delete folders: ' + (error.response?.data?.error || error.message));
                              }
                            }}
                            className="px-3 py-1.5 text-xs bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                          >
                            Delete
                          </button>
                          <button
                            onClick={() => setSelectedFolders([])}
                            className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                          >
                            Clear
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {[...folders].sort((a: any, b: any) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })).map((folder: any) => (
                        <div
                          key={folder.id}
                          onDrop={(e) => handleFolderDrop(e, folder.id)}
                          onDragOver={handleFolderDragOver}
                        >
                          <FolderCard
                            folder={{
                              id: folder.id,
                              name: folder.name,
                              file_count: folder.file_count || 0,
                              created_at: folder.created_at,
                              color: folder.color,
                            }}
                            onClick={() => handleFolderSelect(folder.id)}
                            onContextMenu={(e) => handleFolderContextMenu(folder, e)}
                            isSelected={selectedFolders.includes(folder.id)}
                            onToggleSelect={(e) => {
                              e.stopPropagation();
                              setSelectedFolders(prev =>
                                prev.includes(folder.id)
                                  ? prev.filter(id => id !== folder.id)
                                  : [...prev, folder.id]
                              );
                            }}
                            cardRef={(el) => {
                              if (el) folderRefsMap.current.set(folder.id, el);
                              else folderRefsMap.current.delete(folder.id);
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* FILES */}
                {filteredFiles.length > 0 ? (
                  <div className="relative">

                    {folders.length > 0 && <h2 className="text-lg font-semibold mb-4 mt-8">Files</h2>}
                    {viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {(() => {
                        const totalPages = Math.ceil(filteredFiles.length / filesPerPage);
                        const startIdx = (currentPage - 1) * filesPerPage;
                        const endIdx = startIdx + filesPerPage;
                        const paginatedFiles = filteredFiles.slice(startIdx, endIdx);

                        return paginatedFiles.map((file) => (
                          <Card
                            key={file.id}
                            className="overflow-hidden media-card-item"
                            draggable
                            onDragStart={(e) => handleFileDragStart(e, file.id)}
                            onContextMenu={(e) => handleFileContextMenu(file, e)}
                            ref={(el: HTMLDivElement | null) => {
                              if (el) cardRefsMap.current.set(file.id, el);
                              else cardRefsMap.current.delete(file.id);
                            }}
                          >
                            <div
                              className="aspect-video bg-muted relative cursor-pointer hover:opacity-90 transition-opacity group"
                              onClick={() => {
                                const fileIndex = files.findIndex(f => f.id === file.id);
                                setLightbox({ isOpen: true, initialIndex: fileIndex });
                              }}
                            >
                              {file.thumbnail_url ? (
                                <>
                                  <img
                                    src={file.thumbnail_url}
                                    alt={file.original_filename}
                                    className="w-full h-full object-cover"
                                  />
                                  {/* Play button overlay for videos */}
                                  {file.file_type === 'video' && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                                      <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center group-hover:scale-110 transition-transform">
                                        <div className="w-0 h-0 border-l-[20px] border-l-blue-600 border-y-[12px] border-y-transparent ml-1" />
                                      </div>
                                    </div>
                                  )}
                                  {/* View overlay for images */}
                                  {file.file_type === 'image' && (
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                                      <div className="text-white text-sm font-medium bg-black/50 px-3 py-1 rounded">
                                        Click to view
                                      </div>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="flex items-center justify-center h-full">
                                  {file.file_type === 'image' ? (
                                    <ImageIcon className="w-16 h-16 text-muted-foreground" />
                                  ) : file.file_type === 'video' ? (
                                    <Video className="w-16 h-16 text-muted-foreground" />
                                  ) : file.original_filename.toLowerCase().endsWith('.pdf') ? (
                                    <FileText className="w-16 h-16 text-muted-foreground" />
                                  ) : (
                                    <ImageIcon className="w-16 h-16 text-muted-foreground" />
                                  )}
                                </div>
                              )}
                              {selectionMode && (
                                <div
                                  className="absolute top-2 left-2 bg-white dark:bg-gray-800 rounded p-1 shadow-md border border-gray-200 dark:border-gray-700"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedFiles.includes(file.id)}
                                    onChange={() => toggleFileSelection(file.id)}
                                    className="w-5 h-5 cursor-pointer accent-blue-600"
                                  />
                                </div>
                              )}
                              {/* Star Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleStar(file.id, !file.is_starred);
                                }}
                                className="absolute bottom-2 right-2 p-1.5 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
                                title={file.is_starred ? "Remove star" : "Mark as high performer"}
                              >
                                <Star
                                  className={`w-5 h-5 ${file.is_starred ? 'fill-yellow-400 text-yellow-400' : 'text-white'}`}
                                />
                              </button>
                              <div className="absolute top-2 right-2">
                                <span className="px-2 py-1 text-xs font-medium rounded bg-background/80 backdrop-blur">
                                  {file.file_type}
                                </span>
                              </div>
                            </div>
                            <div className="p-4 space-y-2">
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium truncate flex-1" title={file.original_filename}>
                                  {file.original_filename}
                                </h3>
                                <TestStatusBadge fileId={file.id} />
                              </div>
                              <div className="text-sm text-muted-foreground space-y-1">
                                <p>Editor: {file.editor_name || 'N/A'}</p>
                                <p>Size: {formatBytes(file.file_size)}</p>
                                <p>Uploaded: {formatDate(file.created_at)}</p>
                              </div>
                              <div className="space-y-2">
                                <div className="flex gap-2 pt-3 border-t border-border">
                                  <div className="relative flex-1">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="w-full"
                                      onClick={() => setShowDownloadMenu(showDownloadMenu === file.id ? null : file.id)}
                                    >
                                      <Download className="w-4 h-4 mr-1" />
                                      Download ▼
                                    </Button>
                                    {showDownloadMenu === file.id && (
                                      <div className="absolute left-0 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md shadow-lg z-50">
                                        <button
                                          onClick={() => {
                                            handleDownload(file, false);
                                            setShowDownloadMenu(null);
                                          }}
                                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                                        >
                                          Download Original
                                        </button>
                                        <button
                                          onClick={() => {
                                            handleDownload(file, true);
                                            setShowDownloadMenu(null);
                                          }}
                                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 border-t border-gray-200 dark:border-gray-700"
                                        >
                                          Download Clean (No Metadata)
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1"
                                    onClick={() => handleViewMetadata(file)}
                                    title="View embedded metadata"
                                  >
                                    <Info className="w-4 h-4 mr-1" />
                                    Metadata
                                  </Button>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1"
                                    onClick={() => handleOpenAnalytics(file)}
                                    title="Add analytics metrics"
                                  >
                                    <BarChart3 className="w-4 h-4 mr-1" />
                                    Analytics
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1"
                                    onClick={() => handleFileTags(file)}
                                    title="Manage tags"
                                  >
                                    <Tag className="w-4 h-4 mr-1" />
                                    Tags
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1"
                                    onClick={() => handleToggleStar(file.id, !file.is_starred)}
                                    title={file.is_starred ? 'Remove from starred' : 'Add to starred'}
                                  >
                                    <Star className={`w-4 h-4 mr-1 ${file.is_starred ? 'fill-yellow-500 text-yellow-500' : 'text-gray-400'}`} />
                                    Star
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1"
                                    onClick={() => setShareDialogFile({
                                      id: file.id,
                                      name: file.original_filename,
                                      type: 'file'
                                    })}
                                    title="Share with people or get link"
                                  >
                                    <Share2 className="w-4 h-4 mr-1" />
                                    Share
                                  </Button>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1"
                                    onClick={() => setAddToCollectionModal({
                                      isOpen: true,
                                      fileRequestUploadId: file.id
                                    })}
                                    title="Add to collection"
                                  >
                                    <FolderPlus className="w-4 h-4 mr-1" />
                                    Add to Collection
                                  </Button>
                                </div>
                                <div className="flex gap-2">
                                  {canUpload && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="flex-1"
                                      onClick={() => setVersionHistoryFile({ id: file.id, filename: file.original_filename })}
                                      title="View version history"
                                    >
                                      <Clock className="w-4 h-4 mr-1" />
                                      Versions
                                    </Button>
                                  )}
                                  {canDeleteFile(file) && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="flex-1 text-destructive hover:text-destructive"
                                      onClick={() => setDeleteConfirmId(file.id)}
                                    >
                                      <Trash2 className="w-4 h-4 mr-1" />
                                      Delete
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </Card>
                        ));
                      })()}
                    </div>
                    ) : (
                      /* List View */
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead className="bg-muted/50 sticky top-0 z-10">
                            <tr className="border-b">
                              {selectionMode && (
                                <th className="text-left p-3 w-12">
                                  <input
                                    type="checkbox"
                                    checked={selectedFiles.length === files.length && files.length > 0}
                                    onChange={toggleSelectAll}
                                    className="w-5 h-5 cursor-pointer accent-blue-600"
                                    title="Select all files"
                                  />
                                </th>
                              )}
                              <th className="text-left p-3 w-16"></th>
                              <th className="text-left p-3 font-medium">Filename</th>
                              <th className="text-left p-3 font-medium">Editor</th>
                              <th className="text-left p-3 font-medium">Type</th>
                              <th className="text-left p-3 font-medium">Size</th>
                              <th className="text-left p-3 font-medium">Uploaded</th>
                              <th className="text-left p-3 font-medium w-64">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const totalPages = Math.ceil(filteredFiles.length / filesPerPage);
                              const startIdx = (currentPage - 1) * filesPerPage;
                              const endIdx = startIdx + filesPerPage;
                              const paginatedFiles = filteredFiles.slice(startIdx, endIdx);

                              return paginatedFiles.map((file, index) => (
                                <tr
                                  key={file.id}
                                  ref={(el: HTMLTableRowElement | null) => {
                                    if (el) cardRefsMap.current.set(file.id, el);
                                    else cardRefsMap.current.delete(file.id);
                                  }}
                                  className={`border-b transition-colors hover:bg-muted/50 group ${
                                    index % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                                  } ${selectedFiles.includes(file.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                                  draggable
                                  onDragStart={(e) => handleFileDragStart(e, file.id)}
                                  onContextMenu={(e) => handleFileContextMenu(file, e)}
                                >
                                  {selectionMode && (
                                    <td className="p-3">
                                      <input
                                        type="checkbox"
                                        checked={selectedFiles.includes(file.id)}
                                        onChange={() => toggleFileSelection(file.id)}
                                        className="w-5 h-5 cursor-pointer accent-blue-600"
                                      />
                                    </td>
                                  )}
                                  <td className="p-3">
                                    <div
                                      className="w-12 h-12 rounded overflow-hidden bg-muted flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity relative group"
                                      onClick={() => {
                                        const fileIndex = files.findIndex(f => f.id === file.id);
                                        setLightbox({ isOpen: true, initialIndex: fileIndex });
                                      }}
                                      title="Click to preview"
                                    >
                                      {file.thumbnail_url ? (
                                        <>
                                          <img
                                            src={file.thumbnail_url}
                                            alt={file.original_filename}
                                            className="w-full h-full object-cover"
                                          />
                                          {/* Play icon for videos */}
                                          {file.file_type === 'video' && (
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/20 transition-colors">
                                              <div className="w-6 h-6 rounded-full bg-white/95 flex items-center justify-center">
                                                <div className="w-0 h-0 border-l-[8px] border-l-blue-600 border-y-[5px] border-y-transparent ml-0.5" />
                                              </div>
                                            </div>
                                          )}
                                        </>
                                      ) : (
                                        file.file_type === 'image' ? (
                                          <ImageIcon className="w-6 h-6 text-muted-foreground" />
                                        ) : file.file_type === 'video' ? (
                                          <Video className="w-6 h-6 text-muted-foreground" />
                                        ) : file.original_filename.toLowerCase().endsWith('.pdf') ? (
                                          <FileText className="w-6 h-6 text-muted-foreground" />
                                        ) : (
                                          <ImageIcon className="w-6 h-6 text-muted-foreground" />
                                        )
                                      )}
                                    </div>
                                  </td>
                                  <td className="p-3">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium truncate max-w-xs" title={file.original_filename}>
                                        {file.original_filename}
                                      </span>
                                      {file.is_starred && (
                                        <Star className="w-4 h-4 fill-yellow-500 text-yellow-500 flex-shrink-0" />
                                      )}
                                      <TestStatusBadge fileId={file.id} />
                                    </div>
                                    {file.tags && file.tags.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {file.tags.slice(0, 3).map((tag, idx) => (
                                          <span
                                            key={idx}
                                            className="px-1.5 py-0.5 text-xs rounded bg-accent/20 text-accent-foreground"
                                          >
                                            {tag}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </td>
                                  <td className="p-3 text-sm text-muted-foreground">
                                    {file.editor_name || 'N/A'}
                                  </td>
                                  <td className="p-3">
                                    <span className="px-2 py-1 text-xs font-medium rounded bg-accent/20">
                                      {file.file_type}
                                    </span>
                                  </td>
                                  <td className="p-3 text-sm text-muted-foreground">
                                    {formatBytes(file.file_size)}
                                  </td>
                                  <td className="p-3 text-sm text-muted-foreground">
                                    {formatDate(file.created_at)}
                                  </td>
                                  <td className="p-3">
                                    <div className="flex gap-1">
                                      <button
                                        onClick={() => handleFileTags(file)}
                                        className="p-1.5 hover:bg-accent rounded transition-colors"
                                        title="Manage tags"
                                      >
                                        <Tag className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => setAddToCollectionModal({
                                          isOpen: true,
                                          fileRequestUploadId: file.id
                                        })}
                                        className="p-1.5 hover:bg-accent rounded transition-colors"
                                        title="Add to collection"
                                      >
                                        <FolderPlus className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => handleDownload(file)}
                                        className="p-1.5 hover:bg-accent rounded transition-colors"
                                        title="Download"
                                      >
                                        <Download className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => setShareDialogFile({
                                          id: file.id,
                                          name: file.original_filename,
                                          type: 'file'
                                        })}
                                        className="p-1.5 hover:bg-accent rounded transition-colors"
                                        title="Share"
                                      >
                                        <Share2 className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => handleToggleStar(file.id, !file.is_starred)}
                                        className="p-1.5 hover:bg-accent rounded transition-colors"
                                        title={file.is_starred ? 'Remove from starred' : 'Add to starred'}
                                      >
                                        <Star className={`w-4 h-4 ${file.is_starred ? 'fill-yellow-500 text-yellow-500' : ''}`} />
                                      </button>
                                      {canDeleteFile(file) && (
                                        <button
                                          onClick={() => setDeleteConfirmId(file.id)}
                                          className="p-1.5 hover:bg-destructive/10 rounded transition-colors text-destructive"
                                          title="Delete"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ));
                            })()}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Pagination */}
                    {files.length > filesPerPage && (
                      <div className="flex items-center justify-between mt-6 pt-4 border-t">
                        <p className="text-sm text-muted-foreground">
                          Showing {((currentPage - 1) * filesPerPage) + 1}-{Math.min(currentPage * filesPerPage, files.length)} of {files.length} files
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </Button>
                          <span className="flex items-center px-3 text-sm">
                            Page {currentPage} of {Math.ceil(files.length / filesPerPage)}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.min(Math.ceil(files.length / filesPerPage), p + 1))}
                            disabled={currentPage === Math.ceil(files.length / filesPerPage)}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : folders.length === 0 ? (
                  <Card className="p-12 text-center">
                    <p className="text-muted-foreground">No files or folders found</p>
                  </Card>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showUploadModal && (
        <BatchUploadModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => fetchData(true)}
          editorId={editors.length > 0 ? editors[0].id : ''}
          currentFolderId={currentFolderId}
          editors={editors}
          buyers={buyers}
        />
      )}

      {deleteConfirmId && (
        <DeleteConfirmModal
          fileName={files.find(f => f.id === deleteConfirmId)?.original_filename || 'this file'}
          onConfirm={() => handleDelete(deleteConfirmId)}
          onCancel={() => setDeleteConfirmId(null)}
        />
      )}

      {showBulkEditor && (
        <BulkEditorModal
          selectedFiles={selectedFileObjects}
          onClose={() => setShowBulkEditor(false)}
          onComplete={() => {
            setSelectionMode(false);
            setSelectedFiles([]);
            fetchData();
          }}
        />
      )}

      {metadataViewerFile && (
        <MetadataViewer
          metadata={metadataViewerFile.metadata}
          filename={metadataViewerFile.filename}
          onClose={() => setMetadataViewerFile(null)}
        />
      )}

      {analyticsPanel.isOpen && (
        <AnalyticsMetricsPanel
          fileId={analyticsPanel.fileId}
          fileName={analyticsPanel.fileName}
          initialMetrics={analyticsPanel.currentMetrics}
          onClose={() => setAnalyticsPanel({ isOpen: false, fileId: '', fileName: '', currentMetrics: null })}
          onSave={handleSaveAnalytics}
        />
      )}

      {showCreateFolderModal && (
        <CreateFolderModal
          isOpen={showCreateFolderModal}
          onClose={() => setShowCreateFolderModal(false)}
          onSuccess={fetchData}
          parentFolderId={createFolderParentId}
          parentFolderName={
            createFolderParentId && breadcrumb.length > 0
              ? breadcrumb[breadcrumb.length - 1]?.name
              : undefined
          }
        />
      )}

      <FolderContextMenu
        isOpen={contextMenuFolder !== null}
        position={contextMenuPosition}
        onClose={() => setContextMenuFolder(null)}
        onRename={user?.role === 'buyer' ? undefined : handleRenameFolder}
        onDelete={handleDeleteFolder}
        onCreateSubfolder={handleCreateSubfolder}
        onProperties={handleFolderProperties}
        onShare={handleShareFolder}
        onDownloadZip={handleDownloadFolderZip}
        onToggleLock={handleToggleLock}
        isLocked={contextMenuFolder?.is_locked || false}
      />

      {contextMenuFile && (
        <FileContextMenu
          isOpen={contextMenuFile !== null}
          position={fileContextMenuPosition}
          file={contextMenuFile}
          onClose={() => setContextMenuFile(null)}
          onDownload={handleFileDownload}
          onShare={handleFileShare}
          onStar={handleFileStar}
          onTags={() => handleFileTags()}
          onRename={user?.role === 'buyer' ? undefined : handleFileRename}
          onMove={handleFileMove}
          onCopy={handleFileCopy}
          onVersions={handleFileVersions}
          onActivity={handleFileActivity}
          onProperties={handleFileProperties}
          onComments={handleFileComments}
          onDelete={contextMenuFile && canDeleteFile(contextMenuFile) ? handleFileDelete : undefined}
          onRegenerate={canUseStudio(user) && contextMenuFile?.file_type === 'video'
            ? () => { window.location.href = `/variation-studio?creative=${contextMenuFile.id}`; }
            : undefined}
          isAdmin={isAdmin}
        />
      )}

      {versionHistoryFile && (
        <VersionHistoryModal
          isOpen={true}
          onClose={() => setVersionHistoryFile(null)}
          fileId={versionHistoryFile.id}
          fileName={versionHistoryFile.filename}
          onVersionRestored={fetchData}
        />
      )}

      {activityTimelineFile && (
        <ActivityTimeline
          fileId={activityTimelineFile.id}
          fileName={activityTimelineFile.filename}
          isOpen={true}
          onClose={() => setActivityTimelineFile(null)}
        />
      )}

      {shareDialogFile && (
        <ShareDialog
          isOpen={true}
          onClose={() => setShareDialogFile(null)}
          resourceId={shareDialogFile.id}
          resourceIds={selectedFiles.length > 1 ? selectedFiles : undefined}
          resourceName={shareDialogFile.name}
          resourceType={shareDialogFile.type}
        />
      )}

      <FolderPickerModal
        isOpen={showFolderPicker}
        onClose={() => setShowFolderPicker(false)}
        onSelect={handleFolderPickerSelect}
        title={folderPickerOperation === 'copy' ? `Copy ${selectedFiles.length} File(s)` : `Move ${selectedFiles.length} File(s)`}
        description={folderPickerOperation === 'copy' ? 'Select destination folder to copy files to' : 'Select destination folder to move files to'}
        currentFolderId={currentFolderId}
      />

      <RenameDialog
        isOpen={renameDialog.isOpen}
        onClose={() => setRenameDialog({ ...renameDialog, isOpen: false })}
        onRename={handleRename}
        currentName={renameDialog.currentName}
        resourceType={renameDialog.resourceType}
      />

      <PropertiesPanel
        isOpen={propertiesPanel.isOpen}
        onClose={() => setPropertiesPanel({ ...propertiesPanel, isOpen: false })}
        resourceType={propertiesPanel.resourceType}
        resourceId={propertiesPanel.resourceId}
        onUpdate={fetchData}
      />

      <CommentsPanel
        isOpen={commentsPanel.isOpen}
        onClose={() => setCommentsPanel({ ...commentsPanel, isOpen: false })}
        fileId={commentsPanel.fileId}
        fileName={commentsPanel.fileName}
      />

      <FileTagsManager
        isOpen={tagsManager.isOpen}
        onClose={() => setTagsManager({ ...tagsManager, isOpen: false })}
        mediaId={tagsManager.mediaId}
        fileName={tagsManager.fileName}
        onTagsUpdated={fetchData}
      />

      {/* Media Lightbox */}
      {lightbox.isOpen && files.length > 0 && (
        <MediaLightbox
          files={files}
          initialIndex={lightbox.initialIndex}
          onClose={() => setLightbox({ ...lightbox, isOpen: false })}
          onDelete={async (fileId) => {
            await handleDelete(fileId);
          }}
          onStar={async (fileId) => {
            const file = files.find(f => f.id === fileId);
            if (file) {
              await starredApi.toggleStarred(fileId, file.is_starred || false);
              fetchData();
            }
          }}
          onShare={(file) => {
            setShareDialogFile({
              id: file.id,
              name: file.original_filename,
              type: 'file'
            });
          }}
          onInfo={(file) => {
            setPropertiesPanel({
              isOpen: true,
              resourceType: 'file',
              resourceId: file.id
            });
          }}
          onTags={(file) => {
            setTagsManager({
              isOpen: true,
              mediaId: file.id,
              fileName: file.original_filename
            });
          }}
        />
      )}

      {/* Add to Collection Modal */}
      <AddToCollectionModal
        isOpen={addToCollectionModal.isOpen}
        onClose={() => setAddToCollectionModal({ isOpen: false, fileRequestUploadId: '' })}
        fileRequestUploadId={addToCollectionModal.fileRequestUploadId}
      />
      {/* Download Code Modal — buyer flow when feature is enabled */}
      {downloadCodeFile && (
        <DownloadCodeModal
          mediaFileId={downloadCodeFile.id}
          filename={downloadCodeFile.original_filename}
          onClose={() => setDownloadCodeFile(null)}
          onDownload={(url, fname) => performDownload(url, fname)}
        />
      )}

      <BulkDownloadConfirmModal
        isOpen={bulkDownloadModalState.open}
        onClose={() => setBulkDownloadModalState({ open: false, fileIds: [] })}
        onConfirm={confirmBulkDownloadZip}
        fileIds={bulkDownloadModalState.fileIds}
        context={`${bulkDownloadModalState.fileIds.length} file${bulkDownloadModalState.fileIds.length === 1 ? '' : 's'}`}
      />
    </DashboardLayout>
  );
}

function UploadModal({
  editors,
  currentFolderId,
  onClose,
  onSuccess,
}: {
  editors: Editor[];
  currentFolderId: string | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [editorId, setEditorId] = useState('');
  const [tags, setTags] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [removeMetadata, setRemoveMetadata] = useState(false);
  const [addMetadata, setAddMetadata] = useState(true);
  const [organizeByDate, setOrganizeByDate] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !editorId) {
      setError('Please select a file and editor');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const tagArray = tags.split(',').map((t) => t.trim()).filter(Boolean);
      await mediaApi.upload(
        file,
        editorId,
        tagArray,
        description,
        { removeMetadata, addMetadata },
        { folderId: currentFolderId || undefined, organizeByDate }
      );
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md bg-white dark:bg-gray-900">
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Upload File</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              <X size={20} />
            </button>
          </div>

          {currentFolderId && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                Uploading to current folder
              </p>
            </div>
          )}

          {error && (
            <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium">File</label>
            <Input
              type="file"
              accept="image/*,video/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              required
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">
              Editor {editors.length > 0 && `(${editors.length} available)`}
            </label>
            {editors.length === 0 ? (
              <div className="p-3 rounded-md bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 text-sm">
                No editors found. Please add editors first in the Editors page.
              </div>
            ) : (
              <select
                value={editorId}
                onChange={(e) => setEditorId(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                required
              >
                <option value="">Select editor...</option>
                {editors.map((editor) => (
                  <option key={editor.id} value={editor.id}>
                    {editor.display_name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Tags (comma-separated)</label>
            <Input
              placeholder="campaign, brand-a, product"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Description</label>
            <textarea
              className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm min-h-[80px]"
              placeholder="Optional description..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Folder Options */}
          {!currentFolderId && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={organizeByDate}
                  onChange={(e) => setOrganizeByDate(e.target.checked)}
                  className="w-4 h-4"
                />
                <Calendar className="w-4 h-4" />
                <span className="text-sm">Organize by date (auto-creates jan2024/15-jan/)</span>
              </label>
            </div>
          )}

          {/* Metadata Options */}
          <div className="space-y-3 p-4 rounded-md bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
            <label className="text-sm font-medium block">Metadata Options</label>

            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={removeMetadata}
                  onChange={(e) => setRemoveMetadata(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm">Remove existing metadata before upload</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={addMetadata}
                  onChange={(e) => setAddMetadata(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm">Embed creator metadata (editor name, date, tags)</span>
              </label>
            </div>
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={uploading} className="flex-1">
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function DeleteConfirmModal({
  fileName,
  onConfirm,
  onCancel,
}: {
  fileName: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md bg-white dark:bg-gray-900">
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Delete File</h2>
            <button
              type="button"
              onClick={onCancel}
              className="text-muted-foreground hover:text-foreground"
            >
              <X size={20} />
            </button>
          </div>

          <div className="space-y-3">
            <p className="text-muted-foreground">
              Are you sure you want to delete <strong>{fileName}</strong>?
            </p>
            <p className="text-sm text-muted-foreground">
              This action cannot be undone. The file will be permanently removed from the library.
            </p>
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
              Cancel
            </Button>
            <Button
              type="button"
              className="flex-1 bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              onClick={onConfirm}
            >
              Delete
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function BulkEditorModal({
  selectedFiles,
  onClose,
  onComplete,
}: {
  selectedFiles: MediaFile[];
  onClose: () => void;
  onComplete: () => void;
}) {
  return (
    <BulkMetadataEditor
      selectedFiles={selectedFiles}
      onClose={onClose}
      onComplete={() => {
        onComplete();
        onClose();
      }}
    />
  );
}
